<script lang="ts" setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useOrgSubscriptions } from "@/queries/subscriptions";
import { useSubscribedProducts } from "@/queries/products";
import { useProjects, useProjectsVersionDiffs } from "@/queries/projects";
import type { Project } from "@/types";

type SubscriptionStatus = "Active" | "Expiring soon" | "Expired" | "Inactive";

interface ConsumingProjectView {
  id: string;
  name: string;
  hasUpdate: boolean;
}

interface SubscriptionRow {
  subscriptionId: string;
  productId: string;
  productName: string;
  productDescription: string;
  publisherOrgName: string;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  status: SubscriptionStatus;
  statusBadgeClass: string;
  consumingProjects: ConsumingProjectView[];
  projectsWithUpdates: number;
}

const router = useRouter();
const { subscriptions, subscriptionsLoading, subscriptionsError } = useOrgSubscriptions();
const { subscribedProducts, subscribedProductsLoading, subscribedProductsError } =
  useSubscribedProducts();
const { projects, projectsLoading, projectsError } = useProjects();

// Map productId → projects that consume it. Used both for the "Open project" action and as the
// source of project IDs fed into the batch version-diff query below.
const consumingProjectsByProductId = computed(() => {
  const map = new Map<string, Project[]>();
  for (const project of projects.value) {
    const productId = project.subscriberProductId;
    if (!productId) continue;
    const bucket = map.get(productId);
    if (bucket) bucket.push(project);
    else map.set(productId, [project]);
  }
  return map;
});

// Flat list of every consuming project ID across all subscriptions — fed into useQueries to fire
// one version-diff query per project in parallel. The diff endpoint short-circuits for non-
// subscriber projects on the server, so we don't filter here.
const consumingProjectIds = computed(() =>
  Array.from(consumingProjectsByProductId.value.values()).flat().map(p => p.id)
);

const { diffsByProject } = useProjectsVersionDiffs(consumingProjectIds);

const loading = computed(() =>
  subscriptionsLoading.value || subscribedProductsLoading.value || projectsLoading.value
);

const errorMessage = computed(() => {
  const err = subscriptionsError.value ?? subscribedProductsError.value ?? projectsError.value;
  if (!err) return "";
  return err instanceof Error ? err.message : "Failed to load subscriptions.";
});

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// Status precedence used when sorting cards so the most actionable ones (Active, then Expiring soon)
// surface first; Expired/Inactive sink to the bottom where they're still visible but de-emphasised.
const STATUS_ORDER: Record<SubscriptionStatus, number> = {
  Active: 0,
  "Expiring soon": 1,
  Inactive: 2,
  Expired: 3,
};

function deriveStatus(isActive: boolean, endDate: string | null): SubscriptionStatus {
  if (!isActive) return "Inactive";
  if (endDate) {
    const end = new Date(endDate).getTime();
    const now = Date.now();
    if (end < now) return "Expired";
    if (end - now < THIRTY_DAYS_MS) return "Expiring soon";
  }
  return "Active";
}

function badgeClassFor(status: SubscriptionStatus): string {
  switch (status) {
    case "Active":
      return "bg-success";
    case "Expiring soon":
      return "bg-warning text-dark";
    case "Expired":
      return "bg-danger";
    case "Inactive":
      return "bg-secondary";
  }
}

const rows = computed<SubscriptionRow[]>(() => {
  const productById = new Map(subscribedProducts.value.map(p => [p.id, p]));
  const joined: SubscriptionRow[] = [];
  for (const sub of subscriptions.value) {
    const product = productById.get(sub.productId);
    // A subscription pointing at a product the user can't see (deleted, permissions) is silently
    // dropped — surfacing an unresolved row would be noise without an action the subscriber can take.
    if (!product) continue;
    const status = deriveStatus(sub.isActive, sub.endDate);
    const consumingRaw = consumingProjectsByProductId.value.get(sub.productId) ?? [];
    const consumingProjects: ConsumingProjectView[] = consumingRaw.map(p => ({
      id: p.id,
      name: p.name,
      hasUpdate: diffsByProject.value.get(p.id)?.isUpgradeAvailable === true,
    }));
    const projectsWithUpdates = consumingProjects.filter(p => p.hasUpdate).length;
    joined.push({
      subscriptionId: sub.id,
      productId: sub.productId,
      productName: product.name,
      productDescription: product.description,
      publisherOrgName: product.publisherOrgName,
      startDate: sub.startDate,
      endDate: sub.endDate,
      isActive: sub.isActive,
      status,
      statusBadgeClass: badgeClassFor(status),
      consumingProjects,
      projectsWithUpdates,
    });
  }
  joined.sort((a, b) => {
    const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    if (byStatus !== 0) return byStatus;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });
  return joined;
});

function formatDate(dateString: string | null): string {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function endDateLabel(row: SubscriptionRow): string {
  if (!row.endDate) return "No end date";
  if (row.status === "Expired") return `Expired ${formatDate(row.endDate)}`;
  return `Until ${formatDate(row.endDate)}`;
}

function openProject(projectId: string) {
  router.push(`/projects/${projectId}`);
}
</script>

<template>
  <div class="subscriptions-tab">
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Loading subscriptions…</span>
      </div>
      <p class="text-muted small mt-3 mb-0">Loading subscriptions…</p>
    </div>

    <div v-else-if="rows.length === 0" class="text-center py-5">
      <i class="bi bi-bag-x display-1 text-muted"></i>
      <h3 class="mt-3 text-muted">No active subscriptions</h3>
      <p class="text-muted">
        Once a publisher subscribes your organisation to a product, it will appear here.
      </p>
    </div>

    <div v-else class="row g-4">
      <div
        v-for="row in rows"
        :key="row.subscriptionId"
        class="col-lg-6 col-xl-4"
      >
        <div class="card h-100">
          <div class="card-body d-flex flex-column">
            <div class="d-flex justify-content-between align-items-start mb-2 gap-2">
              <div>
                <h3 class="card-title mb-0">{{ row.productName }}</h3>
                <div class="text-muted small">
                  <i class="bi bi-shop me-1"></i>{{ row.publisherOrgName }}
                </div>
              </div>
              <span class="badge rounded-pill" :class="row.statusBadgeClass">
                {{ row.status }}
              </span>
            </div>

            <p
              v-if="row.productDescription"
              class="text-muted small mb-3"
            >
              {{ row.productDescription }}
            </p>

            <div v-if="row.projectsWithUpdates > 0" class="mb-3">
              <span class="badge bg-info text-dark">
                <i class="bi bi-arrow-up-circle me-1"></i>
                Update available
                <span v-if="row.consumingProjects.length > 1">
                  · {{ row.projectsWithUpdates }} of {{ row.consumingProjects.length }} projects
                </span>
              </span>
            </div>

            <div class="mt-auto small text-muted">
              <div class="mb-1">
                <i class="bi bi-calendar-check me-1"></i>
                Subscribed {{ formatDate(row.startDate) }}
              </div>
              <div class="mb-3">
                <i class="bi bi-hourglass-split me-1"></i>
                {{ endDateLabel(row) }}
              </div>

              <!-- Action area: 0 projects = no button (subscriber hasn't created a project for this
                   product yet); 1 project = direct link; many projects = dropdown so the user can
                   pick which one to open. -->
              <template v-if="row.consumingProjects.length === 1">
                <button
                  class="bb-btn-secondary-cta btn-sm w-100"
                  @click="openProject(row.consumingProjects[0].id)"
                >
                  <i class="bi bi-box-arrow-up-right me-1"></i>
                  Open project
                  <span
                    v-if="row.consumingProjects[0].hasUpdate"
                    class="badge bg-info text-dark ms-2"
                  >
                    Update
                  </span>
                </button>
              </template>
              <template v-else-if="row.consumingProjects.length > 1">
                <div class="dropdown">
                  <button
                    class="bb-btn-secondary-cta btn-sm w-100 dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i class="bi bi-box-arrow-up-right me-1"></i>
                    Open project ({{ row.consumingProjects.length }})
                  </button>
                  <ul class="dropdown-menu w-100">
                    <li v-for="cp in row.consumingProjects" :key="cp.id">
                      <button
                        class="dropdown-item d-flex justify-content-between align-items-center"
                        type="button"
                        @click="openProject(cp.id)"
                      >
                        <span>{{ cp.name }}</span>
                        <span
                          v-if="cp.hasUpdate"
                          class="badge bg-info text-dark ms-2"
                        >
                          Update
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
              </template>
              <template v-else>
                <div class="text-muted fst-italic small">
                  No project uses this subscription yet.
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
